import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '@headlessui/react'
import { User } from 'lucide-react'
import toast from 'react-hot-toast'
import SearchCreateSelect from 'components/common/SearchCreateSelect'
import CreateDealModal from 'components/Modals/CreateDealModal'
import StatCard, { Stat } from 'components/common/StatCard'
import SectionHeader from 'components/common/SectionHeader'
import { useCrmNavigation } from 'hooks/useCrmNavigation'
import {
  useAssociateCompanyContactMutation,
  useCompanyContacts,
  useCompanyDeals,
  useCreateCompanyDealMutation,
  useNonAssociatedCompanyContacts,
} from 'service/crm'
import { DealAssociations, DealFormValues } from 'types/crm/deal'
import { Company } from 'types/crm/company'
import { contactDisplayName } from 'utils/crmContact'
import { formatCloseLabel, formatDealAmount } from 'utils/helpers'
import { BriefcaseIcon, UserIcon } from 'assets/Icons'
import AssociationEmptyState from '../AssociationEmptyState'
import ContactRow from '../ContactRow'
import DealCard, { Deal } from '../Deals/DealCard'
import { AssociatedContact } from '../Deals/DealAssociation'
import { Timestamp } from '../Contacts/ContactAssociation'
import Timestamps from '../Timestamps'
import ContactOptionRow from '../ContactOptionRow'
import { useRecordAccess } from 'context/crm/RecordAccessContext'

const SEARCH_LIMIT = 20

const EmptyRow = ({ children }: { children: React.ReactNode }) => (
  <div className="px-1 py-2.5 text-[13px] text-neutral-faded">{children}</div>
)

type CompanyAssociationProps = {
  companyUuid: string
  stats?: Stat[]
  timestamps?: Timestamp[]
  onAddDeal?: () => void
  onAddContact?: () => void
  /** This company's own display info, so "Create Deal" can pre-select it as a locked association. */
  company?: Pick<Company, 'name' | 'industry' | 'cityLocation' | 'imageUrl'>
}

const CompanyAssociation = ({
  companyUuid,
  stats=[],
  timestamps = [],
  onAddDeal,
  onAddContact,
  company,
}: CompanyAssociationProps) => {
  const [showAllDeals, setShowAllDeals] = useState(false)
  const [showAllContacts, setShowAllContacts] = useState(false)
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false)
  const [showContactSearch, setShowContactSearch] = useState(false)
  const [contactSearchTerm, setContactSearchTerm] = useState('')

  /**
   * RBAC (sections 6, 13, 18): adding a deal or associating a contact are
   * modification actions on THIS company, so they ride on this company's access —
   * not on the associated contact's or deal's own canEdit (sections 20, 21).
   * Viewing the lists, "View all", and clicking through stay open to everyone.
   */
  const { canManageAssociations } = useRecordAccess()

  const { toViewDeal } = useCrmNavigation()

  const { data: dealsData = [], isLoading: isLoadingDeals } =
    useCompanyDeals(companyUuid)
  const { data: contactsData = [], isLoading: isLoadingContacts } =
    useCompanyContacts(companyUuid)

  const createDealMutation = useCreateCompanyDealMutation()
  const associateContactMutation = useAssociateCompanyContactMutation()

  const deals: Deal[] = useMemo(
    () =>
      dealsData.map((deal) => ({
        id: deal.uuid,
        dealKey: deal.dealKey,
        name: deal.name,
        amount:
          deal.amount != null
            ? formatDealAmount(deal.amount, deal.currency || 'INR')
            : '—',
        stage: deal.dealStageLabel,
        closeDate: formatCloseLabel(deal.closeDate),
      })),
    [dealsData],
  )

  const contacts: AssociatedContact[] = useMemo(
    () =>
      contactsData.map((contact) => ({
        uuid: contact.uuid,
        // Carried through so the row can link to /{orgSlug}/crm/{contactKey}.
        contactKey: contact.contactKey,
        name: contactDisplayName(contact),
        email: contact.email,
        profileImageUrl: contact.profileImageUrl ?? null,
      })),
    [contactsData],
  )

  // Only the deal value is real; the rest are mocked until the corresponding
  // endpoints exist — same approach as the deal details panel.
  // const resolvedStats: Stat[] = useMemo(() => {
  //   if (stats) return stats

  //   const total = dealsData.reduce((sum, deal) => sum + (deal.amount ?? 0), 0)
  //   const currency = dealsData.find((deal) => deal.currency)?.currency || 'INR'

  //   return [
  //     {
  //       label: 'Deal value',
  //       value: dealsData.length ? formatDealAmount(total, currency) : '—',
  //       accent: 'success',
  //     },
  //     { label: 'Overdue tasks', value: 2, accent: 'warning' },
  //     { label: 'Activities', value: 6 },
  //     { label: 'Notes', value: 14 },
  //   ]
  // }, [stats, dealsData])

  const {
    contacts: nonAssociatedContacts,
    isLoading: isSearchingContacts,
    isFetchingMore: isFetchingMoreContacts,
    hasMore: hasMoreContacts,
    onLoadMore: loadMoreContacts,
  } = useNonAssociatedCompanyContacts(
    {
      companyUuid,
      search: contactSearchTerm.trim() || undefined,
      limit: SEARCH_LIMIT,
    },
    // Never run the non-associated lookup for a view-only user — it exists only
    // to feed the associate-contact picker they can't open.
    { enabled: showContactSearch && canManageAssociations },
  )

  const contactOptions = useMemo(
    () =>
      nonAssociatedContacts.map((contact) => ({
        uuid: contact.uuid,
        name: contactDisplayName(contact),
        subtitle: [contact.companyName, contact.email]
          .filter(Boolean)
          .join(' · '),
        imageUrl: null,
      })),
    [nonAssociatedContacts],
  )

  // Section 23: access can disappear mid-session. Collapse anything open.
  useEffect(() => {
    if (!canManageAssociations) {
      setShowContactSearch(false)
      setContactSearchTerm('')
      setIsCreateDealModalOpen(false)
    }
  }, [canManageAssociations])

  const handleViewDeal = (deal: Deal) => {
    if (!deal.dealKey) {
      toast.error(`Can't open ${deal.name} — this deal has no reference key.`)
      return
    }
    toViewDeal(deal.dealKey)
  }

  const handleOpenCreateDeal = () => {
    if (!canManageAssociations) return
    setIsCreateDealModalOpen(true)
    onAddDeal?.()
  }

  const handleToggleContactSearch = () => {
    if (!canManageAssociations) return
    setShowContactSearch((prev) => !prev)
    setContactSearchTerm('')
    onAddContact?.()
  }

  const handleCreateDeal = async (
    values: DealFormValues,
    associations: DealAssociations,
  ) => {
    if (!canManageAssociations) {
      toast.error('You do not have permission to add deals to this company.')
      setIsCreateDealModalOpen(false)
      return
    }
    try {
      await createDealMutation.mutateAsync({
        companyUuid,
        dealName: values.name,
        pipeline: values.pipeline || undefined,
        dealStage: values.dealStage || undefined,
        amount: values.amount !== '' ? Number(values.amount) : undefined,
        currency: values.currency || undefined,
        closeDate: values.closeDate || undefined,
        dealOwnerUuid: values.owner || undefined,
        dealType: values.dealType || undefined,
        priority: values.priority || undefined,
        associatedCompanyUuid: companyUuid,
        associatedContactReferences: associations.contactUuids.length
          ? associations.contactUuids
          : undefined,
        associatedCompanyReferences: associations.companyUuids.length
          ? associations.companyUuids
          : undefined,
        description: values.description || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      toast.success('Deal created successfully')
      setIsCreateDealModalOpen(false)
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
        err?.message ??
        'Failed to create deal. Please try again.',
      )
    }
  }

  const handleAssociateContact = (contactUuid: string) => {
    if (!canManageAssociations) {
      toast.error('You do not have permission to change associations on this company.')
      return
    }
    associateContactMutation.mutate(
      { companyUuid, contactUuid },
      {
        onSuccess: () => toast.success('Contact added to company'),
        onError: (err: any) =>
          toast.error(
            err?.response?.data?.message ??
            err?.message ??
            'Failed to add contact. Please try again.',
          ),
      },
    )
  }

  const visibleDeals = showAllDeals ? deals : deals.slice(0, 3)
  const visibleContacts = showAllContacts ? contacts : contacts.slice(0, 3)

  return (
    <>
      <div className="w-full min-h-full rounded-r-xl bg-primary border-l border-primary-subtle">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 p-4 border-b border-primary-subtle">
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>

        {/* Open deals */}
        <div className="p-4 border-b border-primary-subtle">
          <SectionHeader
            title="OPEN DEALS"
            onAdd={canManageAssociations ? handleOpenCreateDeal : undefined}
          />
          <div
            className={`space-y-3 ${showAllDeals ? 'max-h-[530px] overflow-y-auto pr-1' : ''
              }`}
          >
            {visibleDeals.map((deal) => (
              <DealCard
                key={deal.id}
                deal={deal}
                onClick={() => handleViewDeal(deal)}
              />
            ))}
            {deals.length === 0 &&
              (isLoadingDeals ? (
                <EmptyRow>Loading deals…</EmptyRow>
              ) : (
                <AssociationEmptyState
                  icon={<BriefcaseIcon size={20} />}
                  title="No open deals"
                  description="Link a deal to track revenue tied to this company."
                  actionLabel={canManageAssociations ? 'Add deal' : undefined}
                  onAction={canManageAssociations ? handleOpenCreateDeal : undefined}
                />
              ))}
          </div>
          {deals.length > 3 && (
            <Button
              onClick={() => setShowAllDeals((prev) => !prev)}
              className="mt-3 w-full rounded-xl border border-primary-subtle py-2.5 text-center text-[14px] font-medium text-neutral-faded data-active:opacity-70"
            >
              {showAllDeals ? 'Show less' : 'View all'}
            </Button>
          )}
        </div>

        {/* Associated contacts */}
        <div className="p-4 border-b border-primary-subtle">
          <SectionHeader
            title="ASSOCIATED CONTACTS"
            onAdd={canManageAssociations ? handleToggleContactSearch : undefined}
          />
          {showContactSearch && canManageAssociations && (
            <div className="mb-3">
              <SearchCreateSelect
                placeholder="Search to add contacts..."
                listHeading="Existing Contacts"
                emptyLabel="No contacts found"
                icon={User}
                value={contactSearchTerm}
                options={contactOptions}
                isLoading={isSearchingContacts}
                isFetchingMore={isFetchingMoreContacts}
                hasMore={hasMoreContacts}
                onLoadMore={loadMoreContacts}
                hideCreateButton
                showOptionAddAction
                clearOnSelect
                renderOption={(option, select) => (
                  <ContactOptionRow option={option} onSelect={select} />
                )}
                onSearchChange={setContactSearchTerm}
                onSelectOption={(option) => {
                  handleAssociateContact(option.uuid)
                  setContactSearchTerm('')
                }}
              />
            </div>
          )}
          <div
            className={`space-y-3 ${showAllContacts ? 'max-h-[400px] overflow-y-auto pr-1' : ''
              }`}
          >
            {/* ContactRow is read-only today. If an unlink action is added, gate it
              on canManageAssociations (it changes THIS company), not on the
              contact's own canEdit. */}
            {visibleContacts.map((contact) => (
              <ContactRow key={contact.uuid} contact={contact} />
            ))}
            {contacts.length === 0 &&
              (isLoadingContacts ? (
                <EmptyRow>Loading contacts…</EmptyRow>
              ) : (
                <AssociationEmptyState
                  icon={<UserIcon size={20} color="#8B5CF6" />}
                  title="No contact linked"
                  description="Associate a contact to keep the relationship in one place."
                  actionLabel={canManageAssociations ? 'Add contact' : undefined}
                  onAction={canManageAssociations ? handleToggleContactSearch : undefined}
                />
              ))}
          </div>
          {contacts.length > 3 && (
            <Button
              onClick={() => setShowAllContacts((prev) => !prev)}
              className="mt-3 w-full rounded-xl border border-primary-subtle py-2.5 text-center text-[14px] font-medium text-neutral-faded data-active:opacity-70"
            >
              {showAllContacts ? 'Show less' : 'View all'}
            </Button>
          )}
        </div>

        {/* Timestamps */}
        {timestamps.length > 0 && <Timestamps timestamps={timestamps} />}
      </div>

      {canManageAssociations && (
        <CreateDealModal
          isOpen={isCreateDealModalOpen}
          onClose={() => setIsCreateDealModalOpen(false)}
          onSubmit={handleCreateDeal}
          isSaving={createDealMutation.isPending}
          scope={{ type: 'company', uuid: companyUuid }}
          scopeEntity={
            company
              ? {
                name: company.name,
                subtitle: [company.industry, company.cityLocation].filter(Boolean).join(' · '),
                imageUrl: company.imageUrl,
              }
              : undefined
          }
        />
      )}
    </>
  )
}

export default CompanyAssociation